import { generatePath } from 'react-router-dom'
import {
    isAllowed,
    isGlobalAdmin,
    ORDER_OPTIONS_READ,
    ORDER_OPTIONS_WRITE,
    ORDERS_VIEW_ALL_NON_RESTICTED_ORDERS,
} from 'src/utils/authorization'

class RouteError extends Error {
    constructor(msg) {
        super('RouteError: ' + msg)
    }
}

export const routeConfig = [
    {
        path: '/logout',
        title: 'Logout',
        component: './Logout',
        name: 'logout',
        breadcrumbs: [{ label: 'Logout' }],
    },
    {
        path: '/product/wizard',
        title: 'Bulk products cross customers wizard',
        component: './Product/BulkProductsWizard',
        name: 'bulk-product-wizard',
        breadcrumbs: [
            { label: 'Products', linkName: 'product-list' },
            { label: 'Bulk wizard' },
        ],
    },
    {
        path: '/product/create',
        title: 'Create product',
        component: './Product/ProductCreate',
        name: 'product-create',
        breadcrumbs: [{ label: 'Create product' }],
    },
    {
        path: '/product/:id/edit',
        title: 'Edit product',
        component: './Product/ProductEdit',
        name: 'product-edit',
        breadcrumbs: [
            { label: 'Products', linkName: 'product-list' },
            { label: 'Edit product' },
        ],
    },
    {
        path: '/product/:id/api-docs',
        title: 'View API documentation',
        component: './Product/ProductApiDocs',
        name: 'product-api-docs',
        breadcrumbs: [
            { label: 'Products', linkName: 'product-list' },
            { label: 'View API documentation' },
        ],
    },
    {
        path: '/product',
        title: 'Products',
        component: './Product/ProductList',
        name: 'product-list',
        breadcrumbs: [{ label: 'Products' }],
    },
    {
        path: '/product-template/exports',
        title: 'Export product templates',
        component: './ProductTemplate/ProductTemplateExport',
        name: 'productTemplate-export',
        breadcrumbs: [
            { label: 'Product templates', linkName: 'productTemplate-list' },
            { label: 'Export' },
        ],
    },
    {
        path: '/product-template/imports',
        title: 'Import product templates',
        component: './ProductTemplate/ProductTemplateImport',
        name: 'productTemplate-import',
        breadcrumbs: [
            { label: 'Product templates', linkName: 'productTemplate-list' },
            { label: 'Import' },
        ],
    },
    {
        path: '/product-template/create',
        title: 'Create product template',
        component: './ProductTemplate/ProductTemplateCreate',
        name: 'productTemplate-create',
        breadcrumbs: [
            { label: 'Product templates', linkName: 'productTemplate-list' },
            { label: 'Create product template' },
        ],
    },
    {
        path: '/product-template/edit/:id',
        title: 'Edit product template',
        component: './ProductTemplate/ProductTemplateEdit',
        name: 'productTemplate-edit',
        breadcrumbs: [
            { label: 'Product templates', linkName: 'productTemplate-list' },
            { label: 'Edit product template' },
        ],
    },
    {
        path: '/product-template',
        title: 'Product Templates',
        component: './ProductTemplate/ProductTemplateList',
        name: 'productTemplate-list',
        breadcrumbs: [{ label: 'Product templates' }],
    },
    {
        path: '/order-options/export',
        title: 'Order option combo export',
        component: './OrderOption/Export',
        name: 'order-options-export',
        breadcrumbs: [{ label: 'Order options' }, { label: 'Export' }],
        permissions: context =>
            isAllowed(context, ORDER_OPTIONS_READ) || isGlobalAdmin(context),
    },
    {
        path: '/order-options/import',
        title: 'Order option combo import',
        component: './OrderOption/Import',
        name: 'order-options-import',
        breadcrumbs: [{ label: 'Order options' }, { label: 'Import' }],
        permissions: context =>
            isAllowed(context, ORDER_OPTIONS_WRITE) || isGlobalAdmin(context),
    },
    {
        path: '/catalog/create',
        title: 'Create catalog',
        component: './Catalog/CatalogCreate',
        name: 'catalog-create',
        breadcrumbs: [
            { label: 'Catalogs', linkName: 'catalog-list' },
            { label: 'Create' },
        ],
    },
    {
        path: '/catalog/wizard',
        title: 'Create new Catalog wizard',
        component: './Catalog/CatalogWizardRoute',
        name: 'catalog-wizard',
        breadcrumbs: [
            { label: 'Catalogs', linkName: 'catalog-list' },
            { label: 'Wizard' },
        ],
    },
    {
        path: '/catalog/:id/edit',
        title: 'Edit catalog',
        component: './Catalog/CatalogEdit',
        name: 'catalog-edit',
        breadcrumbs: [
            { label: 'Catalogs', linkName: 'catalog-list' },
            { label: 'Edit catalog' },
        ],
    },
    {
        path: '/catalog/:id/:name?/products',
        title: params => `${params.name || 'Catalog'} products`,
        component: './Catalog/CatalogProductList',
        name: 'catalog-products',
        breadcrumbs: params => [
            { label: 'Catalogs', linkName: 'catalog-list' },
            ...(params.name ? [{ label: params.name, linkName: '' }] : []),
            { label: 'Products' },
        ],
    },
    {
        path: '/catalog',
        title: 'Catalogs',
        component: './Catalog/CatalogList',
        name: 'catalog-list',
        breadcrumbs: [{ label: 'Catalogs' }],
    },
    {
        path: '/user/overview',
        title: 'Users',
        component: './UserOverview',
        name: 'user-overview',
        breadcrumbs: [{ label: 'Users' }],
    },
    {
        path: '/category/create',
        title: 'Create category',
        component: './Category/CategoryCreate',
        name: 'category-create',
        breadcrumbs: [
            { label: 'Categories', linkName: 'category-list' },
            { label: 'Create a category' },
        ],
    },
    {
        path: '/category/edit/:id',
        title: 'Edit category',
        component: './Category/CategoryEdit',
        name: 'category-edit',
        breadcrumbs: [
            { label: 'Categories', linkName: 'category-list' },
            { label: 'Edit a category' },
        ],
    },
    {
        path: '/category',
        title: 'Categories',
        component: './Category/CategoryList',
        name: 'category-list',
        breadcrumbs: [{ label: 'Categories' }],
    },
    {
        path: '/group/create',
        title: 'Create group',
        component: './Group/GroupCreate',
        name: 'group-create',
        breadcrumbs: [
            { label: 'Groups', linkName: 'group-list' },
            { label: 'Create group' },
        ],
    },
    {
        path: '/group/edit/:id',
        title: 'Edit group',
        component: './Group/GroupEdit',
        name: 'group-edit',
        breadcrumbs: [
            { label: 'Groups', linkName: 'group-list' },
            { label: 'Edit group' },
        ],
    },
    {
        path: '/group',
        title: 'Groups',
        component: './Group/GroupList',
        name: 'group-list',
        breadcrumbs: [{ label: 'Groups' }],
    },
    {
        path: '/macro/exports',
        title: 'Export macros',
        component: './Macro/MacroExport',
        name: 'macro-export',
        breadcrumbs: [
            { label: 'Macro', linkName: 'macro-list' },
            { label: 'Export' },
        ],
    },
    {
        path: '/macro/imports',
        title: 'Import macros',
        component: './Macro/MacroImport',
        name: 'macro-import',
        breadcrumbs: [
            { label: 'Macro', linkName: 'macro-list' },
            { label: 'Import' },
        ],
    },
    {
        path: '/macro/create',
        title: 'Create macro',
        component: './Macro/MacroCreate',
        name: 'macro-create',
        breadcrumbs: [
            { label: 'Macros', linkName: 'macro-list' },
            { label: 'Create macro' },
        ],
    },
    {
        path: '/macro/edit/:id',
        title: 'Edit macro',
        component: './Macro/MacroEdit',
        name: 'macro-edit',
        breadcrumbs: [
            { label: 'Macros', linkName: 'macro-list' },
            { label: 'Edit macro' },
        ],
    },
    {
        path: '/macro',
        title: 'Macros',
        component: './Macro/MacroList',
        name: 'macro-list',
        breadcrumbs: [{ label: 'Macros' }],
    },
    {
        path: '/order/:id',
        title: params => `Order ${params.id}`,
        component: './Order/OrderDetail',
        name: 'order',
        breadcrumbs: params => [
            { label: 'Orders', linkName: 'order-list' },
            { label: `Order ${params.id}` },
        ],
        permissions: context =>
            isAllowed(context, ORDERS_VIEW_ALL_NON_RESTICTED_ORDERS),
    },
    {
        path: '/order',
        title: 'Orders',
        component: './Order/OrderList',
        name: 'order-list',
        breadcrumbs: [{ label: 'Orders' }],
        permissions: context =>
            isAllowed(context, ORDERS_VIEW_ALL_NON_RESTICTED_ORDERS),
    },
    {
        path: '/',
        title: 'Homepage',
        component: './Home',
        name: 'home',
        breadcrumbs: [{ label: 'Dashboard' }],
    },
]

export const reverse = (name, params = {}, queryParams = {}) => {
    const routes = routeConfig.filter(route => route.name === name)
    if (routes.length === 0) {
        throw new RouteError(`No matches found in routeConfig for ${name}`)
    } else if (routes.length > 1) {
        throw new RouteError(
            `Multiple matches found in routeConfig for ${name}: ${routes
                .map(r => r.path)
                .join(', ')}`
        )
    }
    // this will raise a TypeError if params provided do not match the path
    const path = generatePath(routes[0].path, params)
    if (Object.entries(queryParams).length) {
        const searchParams = new URLSearchParams(queryParams)
        return `${path}?${searchParams.toString()}`
    }
    return path
}
